import { MinusIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@/app/hooks';
import { PreferencesActions } from '@/app/slices/preferences';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Sheet } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { getAllLanguages, getLanguageByKey } from '@/languages';
import { cn } from '@/lib/utils';
import { SCALES } from '@/utils/preferences';

export const PreferencesSheet = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { show, scale, compactGrid } = useAppSelector(
    (state) => state.preferences,
  );
  const languages = getAllLanguages();
  const language = getLanguageByKey(
    i18n.language ?? i18n.resolvedLanguage,
  )?.value;

  const setScale = (value: number) =>
    dispatch({ type: PreferencesActions.UPDATE_SCALE, scale: value });

  return (
    <Sheet
      open={show}
      onOpenChange={(open) =>
        dispatch({ type: PreferencesActions.UPDATE_SHOW, show: open })
      }
      title={t('nav.preferences', 'Preferences')}
    >
      <div className="flex flex-col gap-5 py-1">
        <Field>
          <FieldLabel htmlFor="preferences-language">
            {t('preferences.language', 'Language')}
          </FieldLabel>
          <Select
            value={language ?? null}
            onChange={(value) => value && i18n.changeLanguage(String(value))}
            items={languages}
          >
            <SelectTrigger id="preferences-language" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languages.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Separator />

        <Field>
          <FieldLabel>{t('preferences.textSize', 'Text size')}</FieldLabel>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label={t('preferences.smaller', 'Smaller')}
              disabled={scale <= SCALES[0]}
              onClick={() => setScale(scale - 1)}
            >
              <MinusIcon />
            </Button>
            <div className="flex flex-1 items-center justify-between px-1">
              {SCALES.map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-label={`${value}px`}
                  aria-pressed={value === scale}
                  onClick={() => setScale(value)}
                  className="flex size-8 items-center justify-center"
                >
                  <span
                    className={cn(
                      'block size-2.5 rounded-full bg-gray-300 transition-transform',
                      value === scale && 'bg-primary scale-150',
                    )}
                  />
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="icon"
              aria-label={t('preferences.larger', 'Larger')}
              disabled={scale >= SCALES[SCALES.length - 1]}
              onClick={() => setScale(scale + 1)}
            >
              <PlusIcon />
            </Button>
          </div>
        </Field>

        <Separator />

        <Field orientation="horizontal" className="justify-between">
          <div className="flex flex-col gap-1">
            <FieldLabel htmlFor="preferences-compact-grid">
              {t('preferences.compactGrids', 'Compact grids')}
            </FieldLabel>
            <FieldDescription>
              {t(
                'preferences.compactGridsDescription',
                'Tighter row height in data grids.',
              )}
            </FieldDescription>
          </div>
          <Switch
            id="preferences-compact-grid"
            checked={compactGrid}
            onCheckedChange={(checked) =>
              dispatch({
                type: PreferencesActions.UPDATE_COMPACT_GRID,
                compactGrid: checked,
              })
            }
          />
        </Field>
      </div>
    </Sheet>
  );
};
